import type { PostHogConfig } from 'posthog-js-v2';

export type PosthogV2Config = Partial<PostHogConfig> & {
    projectId: string;
    api_host: string;
};

export type PosthogV2Bootstrap = NonNullable<PostHogConfig['bootstrap']>;

/**
 * Isolation and identity options for the second (temporary) PostHog instance.
 *
 * Both bundled copies of posthog-js share `window.__PosthogExtensions__` (the lazy loader and
 * registry for recorder / surveys / dead-clicks extensions) and `window._POSTHOG_REMOTE_CONFIG`.
 * To keep the two versions from stepping on each other, the V2 instance never fetches or consumes
 * any extension script: `disable_external_dependency_loading` stops it from loading scripts and the
 * feature flags stop it from wanting to. `autocapture` / pageview / pageleave mirror the existing
 * instance's configuration so both projects receive the same set of events.
 *
 * Identity: the distinct id is bootstrapped from the session prefix at init (see
 * `buildPosthogV2Bootstrap`) and the instance never identifies users. `person_profiles: 'never'`
 * makes posthog-js ignore any `identify()` call, and `persistence: 'memory'` keeps the instance
 * from writing cookies or localStorage entries that the legacy instance also uses.
 */
export const POSTHOG_V2_ISOLATION_OPTIONS = {
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    disable_session_recording: true,
    disable_surveys: true,
    disable_external_dependency_loading: true,
    persistence: 'memory',
    person_profiles: 'never',
} as const satisfies Partial<PostHogConfig>;

const UUID_V7_PATTERN =
    /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const isUuidV7 = (value: string): boolean => UUID_V7_PATTERN.test(value);

/**
 * Builds the `bootstrap` init option that pins the PostHog identity to the widget session prefix,
 * mirroring how the checkout app derives its PostHog distinct id from the same session id.
 * The prefix is also used as the PostHog session id when it is a valid UUID v7, which posthog-js
 * requires for bootstrapped session ids; otherwise only the distinct id is bootstrapped.
 *
 * @param distinctId - The session prefix to use as the anonymous distinct id
 * @returns The bootstrap options for `posthog.init`
 */
export const buildPosthogV2Bootstrap = (
    distinctId: string
): PosthogV2Bootstrap =>
    isUuidV7(distinctId)
        ? { distinctID: distinctId, sessionID: distinctId }
        : { distinctID: distinctId };

const isNonEmptyString = (value: unknown): value is string =>
    typeof value === 'string' && value.trim().length > 0;

export const isPosthogV2Configured = (
    projectId: unknown,
    apiHost: unknown
): boolean => isNonEmptyString(projectId) && isNonEmptyString(apiHost);

export const buildPosthogV2Config = (
    projectId: unknown,
    apiHost: unknown
): PosthogV2Config | null => {
    if (!isNonEmptyString(projectId) || !isNonEmptyString(apiHost)) {
        return null;
    }
    return {
        projectId: projectId.trim(),
        api_host: apiHost.trim(),
        ...POSTHOG_V2_ISOLATION_OPTIONS,
    };
};
